// regional forecast and observations
import STATUS, { calcStatusClass, statusClasses } from './status.mjs';
import WeatherDisplay from './weatherdisplay.mjs';
import {
	registerProgress, message, getDisplay, msg,
} from './navigation.mjs';

class Progress extends WeatherDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, '', false);

		// disable any navigation timing
		this.timing = false;

		// setup event listener for dom-required initialization
		document.addEventListener('DOMContentLoaded', () => {
			this.version = document.querySelector('#version').innerHTML.replace(/\s/g, '');
			this.elem.querySelector('.container').addEventListener('click', this.lineClick.bind(this));
		});

		this.okToDrawCurrentConditions = false;

		// OBX Airwaves: retain original large lettering and page the menu.
		this.obxPage = 0;
		this.obxPageSize = 9; // nine entries + one page-control row fit above the ticker
		this.obxLastDisplays = null;
		this.obxLastLoadedCount = 0;
	}

	async drawCanvas(displays, loadedCount) {
		// skip drawing if not displayed, or not yet available
		if (!this.elem) return;
		if (this.elem.classList.contains('show') === false) return;
		super.drawCanvas();

		// get the progress bar cover (makes percentage)
		if (!this.progressCover) this.progressCover = this.elem.querySelector('.scroll .cover');

		// if no displays provided just draw the backgrounds (above)
		if (!displays) return;
		// A WeatherStar menu is only 310 px tall. The original code appended
		// everything, so entries beyond the tenth were hidden behind the ticker.
		// Filter portrait-only slides BEFORE paging, keeping original nav indexes.
		this.obxLastDisplays = displays;
		this.obxLastLoadedCount = loadedCount;
		const portrait = Boolean(this.elem.closest('.portrait.enhanced'));
		const rows = displays.flatMap((display, index) => (
			display && (portrait || display.showOnProgress) ? [{ display, index }] : []
		));
		const pageCount = portrait ? 1 : Math.max(1, Math.ceil(rows.length / this.obxPageSize));
		this.obxPage = Math.min(this.obxPage, pageCount - 1);
		const visibleRows = pageCount === 1 ? rows
			: rows.slice(this.obxPage * this.obxPageSize, (this.obxPage + 1) * this.obxPageSize);
		const lines = visibleRows.map(({ display, index }) => {
			const line = this.fillTemplate('item', { name: display.name });
			if (!line) return null; // template might not have loaded yet
			const links = line.querySelector('.links');
			links.classList.remove(...statusClasses);
			links.classList.add(calcStatusClass(display.status));
			links.dataset.index = index; // ORIGINAL index, not page-relative
			return line;
		}).filter(Boolean);

		const container = this.elem.querySelector('.container');
		container.innerHTML = '';
		container.append(...lines);

		// Use real buttons, so clicks, keyboard activation and focus work.
		// The pager is hidden entirely when every menu entry already fits.
		if (pageCount > 1) {
			const pager = document.createElement('div');
			pager.className = 'obx-progress-pager';
			const prev = document.createElement('button');
			prev.type = 'button';
			prev.dataset.pageMove = '-1';
			prev.textContent = '<< PREV';
			prev.disabled = this.obxPage === 0;
			prev.setAttribute('aria-label', 'Previous menu page');
			const label = document.createElement('span');
			label.className = 'obx-progress-page-count';
			label.textContent = `PAGE ${this.obxPage + 1}/${pageCount}`;
			const next = document.createElement('button');
			next.type = 'button';
			next.dataset.pageMove = '1';
			next.textContent = 'NEXT >>';
			next.disabled = this.obxPage === pageCount - 1;
			next.setAttribute('aria-label', 'Next menu page');
			pager.append(prev, label, next);
			container.append(pager);
		}

		this.finishDraw();

		// calculate loaded percent
		const loadedPercent = (loadedCount / displays.length);

		this.progressCover.style.width = `${(1.0 - loadedPercent) * 100}%`;
		if (loadedPercent < 1.0) {
			// show the progress bar and set width
			this.progressCover.parentNode.classList.add('show');
		} else {
			// hide the progressbar after 1 second (lines up with with width transition animation)
			setTimeout(() => this.progressCover.parentNode.classList.remove('show'), 1000);
		}
	}

	lineClick(e) {
		// The controls do NOT enter the weather navigation engine.
		const pagerButton = e.target?.closest?.('button[data-page-move]');
		if (pagerButton) {
			if (pagerButton.disabled) return;
			this.obxPage += Number(pagerButton.dataset.pageMove);
			this.drawCanvas(this.obxLastDisplays, this.obxLastLoadedCount);
			return;
		}

		// get index
		const indexRaw = e.target?.parentNode?.dataset?.index;
		if (indexRaw === undefined) return;
		const index = +indexRaw;

		// stop playing
		message('navButton');
		// use the y value to determine an index
		const display = getDisplay(index);
		if (display && display.status === STATUS.loaded) {
			display.showCanvas(msg.command.firstFrame);
			this.elem.classList.remove('show');
		}
	}
}

// register our own display
const progress = new Progress(-1, 'progress');
registerProgress(progress);

const version = () => progress.version;

export default progress;
export {
	version,
};
